'use es6';

import { Fragment, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedPhoneNumber from 'I18n/components/FormattedPhoneNumber';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import unescapedText from 'I18n/utils/unescapedText';
import UIIcon from 'UIComponents/icon/UIIcon';
import UICopyButton from 'UIComponents/button/UICopyButton';
import UILink from 'UIComponents/link/UILink';
import UIList from 'UIComponents/list/UIList';
import { getEventSelectorId } from '../../../../utils/analytics';
import { getFormattedUrl } from '../../../../utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const I18N_PATH = 'appMarketplace.detail.overview.sidebar.contact';
const Link = props => /*#__PURE__*/_jsx(UILink, Object.assign({}, props, {
  external: true,
  iconProps: {
    className: 'hidden'
  }
}));
const Contact = ({
  chatUrl,
  companyUrl,
  email,
  facebookUrl,
  hasZorseChatScope,
  hasZorseWebticketScope,
  homemade,
  isCustomer,
  onTrackInteraction,
  openHelpWidget,
  phone
}) => {
  const trackInteraction = useCallback(action => onTrackInteraction(action, 'contact'), [onTrackInteraction]);
  const renderedEmailSupportLink = useMemo(() => {
    const emailSupportIconText = /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(UIIcon, {
        name: "email"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${I18N_PATH}.emailSupportLinkText`
      })]
    });
    if (homemade) {
      if (isCustomer && hasZorseWebticketScope) {
        return /*#__PURE__*/_jsx(Link, {
          "data-hs-analytics-id": getEventSelectorId('SIDEBAR-EMAIL-SUPPORT'),
          onClick: () => {
            trackInteraction('click copy email address');
            openHelpWidget({
              create: true
            });
          },
          children: emailSupportIconText
        });
      } else {
        return null;
      }
    }
    return /*#__PURE__*/_jsx(UICopyButton, {
      "data-hs-analytics-id": getEventSelectorId('SIDEBAR-EMAIL-SUPPORT'),
      hint: unescapedText(`${I18N_PATH}.emailCopyButtonHint`),
      onClick: () => trackInteraction('click copy email address'),
      value: email,
      use: "link",
      children: emailSupportIconText
    });
  }, [email, hasZorseWebticketScope, homemade, isCustomer, openHelpWidget, trackInteraction]);
  const renderedLiveChatLink = useMemo(() => {
    const liveChatIconText = /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(UIIcon, {
        name: "messages"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${I18N_PATH}.liveChatLinkText`
      })]
    });
    if (homemade) {
      if (isCustomer && hasZorseChatScope) {
        return /*#__PURE__*/_jsx(Link, {
          "data-hs-analytics-id": getEventSelectorId('SIDEBAR-LIVE-CHAT'),
          onClick: () => {
            trackInteraction('click live chat link');
            openHelpWidget({
              chat: true
            });
          },
          children: liveChatIconText
        });
      } else {
        return null;
      }
    }
    return chatUrl && /*#__PURE__*/_jsx(Link, {
      "data-hs-analytics-id": getEventSelectorId('SIDEBAR-LIVE-CHAT'),
      href: getFormattedUrl(chatUrl),
      onClick: () => trackInteraction('click live chat link'),
      children: liveChatIconText
    });
  }, [chatUrl, hasZorseChatScope, homemade, isCustomer, openHelpWidget, trackInteraction]);
  const renderedPhoneNumber = useMemo(() => {
    if (!phone) return null;
    const [phoneNumberBase, extension] = phone.split('-');
    const formattedPhoneNumberBase = /*#__PURE__*/_jsx(FormattedPhoneNumber, {
      value: phoneNumberBase
    });
    return extension ? /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(Link, {
        href: `tel:${phoneNumberBase}`,
        children: /*#__PURE__*/_jsx(UIIcon, {
          name: "calling"
        })
      }), /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: `${I18N_PATH}.phoneWithExtension`,
        options: {
          phoneNumberBase: /*#__PURE__*/_jsx(Link, {
            href: `tel:${phoneNumberBase}`,
            children: formattedPhoneNumberBase
          }),
          extension
        }
      })]
    }) : /*#__PURE__*/_jsxs(Link, {
      href: `tel:${phoneNumberBase}`,
      children: [/*#__PURE__*/_jsx(UIIcon, {
        name: "calling"
      }), formattedPhoneNumberBase]
    });
  }, [phone]);
  return /*#__PURE__*/_jsxs(UIList, {
    children: [renderedEmailSupportLink, companyUrl && /*#__PURE__*/_jsxs(Link, {
      "data-hs-analytics-id": getEventSelectorId('SIDEBAR-COMPANY'),
      href: getFormattedUrl(companyUrl),
      onClick: () => trackInteraction('click company website link'),
      children: [/*#__PURE__*/_jsx(UIIcon, {
        name: "website"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${I18N_PATH}.companyWebsiteLinkText`
      })]
    }), renderedLiveChatLink, facebookUrl && /*#__PURE__*/_jsxs(Link, {
      "data-hs-analytics-id": getEventSelectorId('SIDEBAR-FACEBOOK'),
      href: getFormattedUrl(facebookUrl),
      onClick: () => trackInteraction('click facebook link'),
      children: [/*#__PURE__*/_jsx(UIIcon, {
        name: "socialBlockFacebook"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${I18N_PATH}.facebookLinkText`
      })]
    }), renderedPhoneNumber]
  });
};
Contact.propTypes = {
  chatUrl: PropTypes.string,
  companyUrl: PropTypes.string,
  email: PropTypes.string.isRequired,
  facebookUrl: PropTypes.string,
  hasZorseChatScope: PropTypes.bool.isRequired,
  hasZorseWebticketScope: PropTypes.bool.isRequired,
  homemade: PropTypes.bool.isRequired,
  isCustomer: PropTypes.bool.isRequired,
  onTrackInteraction: PropTypes.func.isRequired,
  openHelpWidget: PropTypes.func.isRequired,
  phone: PropTypes.string
};
export default Contact;